import { useState, useEffect } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import {
  FORMAT_OPTIONS,
  DEFAULT_FORMAT,
  type ArtifactType,
} from "~/components/assistant/messages/artifactFormats";
import { Figure, Dataframe, DataValue } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { useDownloadArtifact } from "./hooks/useDownloadArtifact";

type DownloadStage = "select" | "downloading" | "error";

interface DownloadDialogProps {
  open: boolean;
  onClose: () => void;
  artifact: Figure | Dataframe | DataValue;
  artifactType: ArtifactType;
}

export function DownloadDialog({ open, onClose, artifact, artifactType }: DownloadDialogProps) {
  const [stage, setStage] = useState<DownloadStage>("select");
  const [selectedFormat, setSelectedFormat] = useState(DEFAULT_FORMAT[artifactType]);
  const [error, setError] = useState<string | undefined>();
  const { contextId } = useAssistantContext();
  const { downloadArtifact } = useDownloadArtifact();

  useEffect(() => {
    if (open) {
      setStage("select");
      setSelectedFormat(DEFAULT_FORMAT[artifactType]);
      setError(undefined);
    }
  }, [open, artifactType]);

  const handleDownload = async () => {
    if (!artifact.artifact_id) return;

    setStage("downloading");

    const result = await downloadArtifact({
      artifactId: artifact.artifact_id,
      artifactType,
      format: selectedFormat,
      contextId,
    });

    if (result.success) {
      onClose();
    } else {
      setError(result.error || "Something went wrong while downloading your file.");
      setStage("error");
    }
  };

  const handleRetry = () => {
    setError(undefined);
    setStage("select");
  };

  const options = FORMAT_OPTIONS[artifactType];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        {stage === "select" && (
          <>
            <DialogHeader>
              <DialogTitle>Download artifact</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">Choose a format for your download:</p>
              <RadioGroup value={selectedFormat} onValueChange={setSelectedFormat}>
                {options.map((opt) => (
                  <RadioGroupItem
                    key={opt.value}
                    value={opt.value}
                    label={opt.label}
                    description={opt.description}
                    variant="card"
                    className="w-full"
                  />
                ))}
              </RadioGroup>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleDownload}>Download</Button>
            </div>
          </>
        )}

        {stage === "downloading" && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <Loader2 className="size-10 text-primary animate-spin" />
            <p className="text-base font-medium">Preparing download...</p>
            <p className="text-sm text-gray-500">This may take a moment for large files.</p>
          </div>
        )}

        {stage === "error" && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="rounded-full bg-red-100 p-5">
              <AlertCircle className="size-8 text-red-600" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-lg font-bold">Unable to download</p>
              <p className="text-sm text-gray-600">
                {error || "Something went wrong while downloading your file."}
              </p>
            </div>
            <div className="flex flex-col items-center mt-2 gap-1">
              <Button onClick={handleRetry} className="px-6">
                Retry
              </Button>
              <Button variant="link" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
